/*
 * Copyright (c) 2023
 *
 * The source code and all documents related to the source code ("Material") are owned by
 * Frictionless Systems, LLC.
 *
 * The Material contains trade secrets, proprietary information, and confidential information.
 * No part of the Material may be used, copied, reproduced, modified, published, distributed, or
 * disclosed in any way without Frictionless Systems, LLC’s express prior written permission.
 *
 * The Material may be protected under patent, trademark, copyright, or trade secret laws.  No
 * license is granted to you by disclosure or delivery of the Materials.  Any license must be
 * express and approved by Frictionless Systems, LLC in writing.
 *
 * “Frictionless Systems” and “Impulse” are registered trademarks of Frictionless Systems, LLC.
 */

package systems.frictionless.impulse.browser.routing

import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import org.w3c.dom.HashChangeEvent
import org.w3c.dom.Window

/**
 * Simple Routing helper.  By design, the routing only works off of the trailing fragment of the URL.
 *
 * @param windowObjectProxy browser window object proxy
 * @param screensList provides all possible pages. Typically there is [Enum.values()] used.
 * @param defaultScreen page shown when there is no hash or hash is not
 *        recognized.
 * @param initialScreen The starting screen, which might be different from the different from the [defaultScreen] if
 * there was a deep link to bring the user to a specific page.
 */
class Router<E> private constructor(
    private val windowObjectProxy: WindowObjectProxy,
    private val screensList: Array<E>,
    private val defaultScreen: E,
    initialScreen: E
) where E : Enum<E>, E : Routable {
    /**
     * Mapping of hash key to Routable.
     */
    private val routeMap = newRouteMap(screensList, defaultScreen)

    private val mutableCurrentPage = MutableStateFlow(initialScreen)

    val currentPage: StateFlow<E>
        get() = mutableCurrentPage

    init {
        // This ensures the browser address bar is in sync with the initial screen
        navigate(initialScreen)

        windowObjectProxy.onhashchange = {
            onUrlChanged()
        }
    }

    /**
     * Navigates to different page.
     *
     * @param target new page
     * @param isAddToHistory True if the current page should be added to the history.
     */
    fun navigate(
        target: E,
        isAddToHistory: Boolean = false
    ) {
        val newHash = target.getTargetHash()

        if (newHash == windowObjectProxy.hash) {
            return
        }

        if (isAddToHistory) {
            windowObjectProxy.push(newHash)
        } else {
            windowObjectProxy.replace(newHash)
        }

        mutableCurrentPage.value = target
    }

    private fun Routable.getTargetHash() = if (this == defaultScreen) "" else hash

    /*
     * This is called when there's an implicit request to navigate to a new target.  E.g. this could be called if a
     * hyperlink is clicked within our UI which contains a hash.  This is generally not called if the user manipulates
     * the address bar, as that would be a new page load.
     */
    private fun onUrlChanged() {
        val newHash = windowObjectProxy.hash

        val newRoute = routeMap.getOrElse(newHash) { defaultScreen }

        if (newRoute == mutableCurrentPage.value) {
            return
        }

        mutableCurrentPage.value = newRoute
    }

    companion object {
        /**
         * This method is not part of the public API, but must be public as a helper to the reified inline fun
         */
        fun <E> neverCallThisMethodItIsForInternalUsageOnly(
            windowObjectProxy: WindowObjectProxy,
            allEnumValues: Array<E>,
            defaultScreen: E,
            initialScreen: E = defaultScreen
        ) where E : Enum<E>, E : Routable =
            Router(
                windowObjectProxy = windowObjectProxy,
                screensList = allEnumValues,
                defaultScreen = defaultScreen,
                initialScreen = initialScreen
            )

        /**
         * Creates new instance of [Router].
         *
         * @param windowObjectProxy browser window object proxy
         * @param defaultScreen default screen (if no has is presented in URL)
         */
        inline operator fun <reified E> invoke(
            windowObjectProxy: WindowObjectProxy,
            defaultScreen: E,
            initialScreen: E = defaultScreen
        ): Router<E> where E : Enum<E>, E : Routable {
            return neverCallThisMethodItIsForInternalUsageOnly(
                windowObjectProxy = windowObjectProxy,
                allEnumValues = enumValues(),
                defaultScreen = defaultScreen,
                initialScreen = initialScreen
            )
        }

        /**
         * @return A mapping of route hash to route.
         */
        private fun <E> newRouteMap(
            routes: Array<E>,
            defaultRoute: E
        ) where E : Enum<E>, E : Routable =
            buildMap<String, E> {
                putAll(routes.associateBy { it.hash })
                put("", defaultRoute)
            }
    }
}

/**
 * Default implementation of [org.w3c.dom.Window].
 */
class BrowserWindowObjectProxy(val window: Window) : WindowObjectProxy {
    override fun replace(hash: String) {
        val url = newUrl(hash)

        window.history.replaceState(null, "", url)
    }

    override fun push(hash: String) {
        val url = newUrl(hash)

        window.history.pushState(null, "", url)
    }

    private fun newUrl(hash: String) =
        window.location.origin.let {
            if (hash.isBlank()) {
                it
            } else {
                "$it/#$hash"
            }
        }

    override var onhashchange: ((HashChangeEvent) -> dynamic)?
        get() = window.onhashchange
        set(value) {
            window.onhashchange = value
        }

    override val hash: String
        get() = window.location.hash.removePrefix("#")

    override val queryString: String
        get() = window.location.search
}

/**
 * Typically proxies [org.w3c.dom.Window] but you may use different implementation while unit testing.
 * @see BrowserWindowObjectProxy
 */
interface WindowObjectProxy {
    /** Replaces current hash with new value. Value is not stored in the history. */
    fun replace(hash: String)

    /** Replaces current hash with new value. Value is stored in the history. */
    fun push(hash: String)

    /** [HashChangeEvent] listener. */
    var onhashchange: ((HashChangeEvent) -> dynamic)?

    /** Current value of hash*/
    val hash: String

    /**
     * Current value of query string.
     */
    val queryString: String
}
